import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/pages/_subscriber-management.scss';
import { API_CONFIG } from '../config/api';
import { formatDate } from '../utils/dateFormatter';


// Loading Spinner Component
const LoadingSpinner: React.FC = () => (
  <div className="subscriber-management__spinner">
    <div className="subscriber-management__spinner-circle"></div>
  </div>
);

interface Subscriber {
  id: string;
  userId: string;
  name: string;
  email: string;
  plan: string;
  hasSubscription: boolean;
  startDate: string | null;
  endDate: string | null;
  status: 'active' | 'expired' | 'cancelled' | 'free';
  subscriptionId: string | null;
}

// API response interface
interface SubscribersResponse {
  users: Subscriber[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalUsers: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

const SubscriberManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showDateDropdown, setShowDateDropdown] = useState(false);
  const [selectedSubscribers, setSelectedSubscribers] = useState<Set<string>>(new Set());
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showExtendModal, setShowExtendModal] = useState(false);
  const [extendDays, setExtendDays] = useState('');
  const [extendPlanId, setExtendPlanId] = useState<string>('');
  const [availablePlans, setAvailablePlans] = useState<any[]>([]);
  const [subscribers, setSubscribers] = useState<Subscriber[]>([]);
  const [pagination, setPagination] = useState<SubscribersResponse['pagination'] | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [notification, setNotification] = useState<{type: 'success' | 'error' | 'info', message: string} | null>(null);

  // Fetch subscribers from API
  const fetchSubscribers = useCallback(async (page: number = 1, search: string = '', status: string = 'all', dateFrom: string = '', dateTo: string = '') => {
    try {
      setIsFetching(true);

      const params = new URLSearchParams({
        page: page.toString(),
        limit: '5', // items per page
      });

      if (search) params.append('search', search);
      if (status !== 'all') params.append('status', status);
      if (dateFrom) params.append('dateFrom', dateFrom);
      if (dateTo) params.append('dateTo', dateTo);


      const response = await fetch(`${API_CONFIG.ENDPOINTS.USERS_ADMIN_ALL}?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch subscribers');
      }

      const fullResponse = await response.json();

      // The backend returns { success: true, data: { users: [...], pagination: {...} } }
      const data = fullResponse.data;

      setSubscribers(data?.users || []);
      setPagination(data?.pagination || null);

      // Sync currentPage with API response if it differs
      if (data?.pagination?.currentPage && data.pagination.currentPage !== currentPage) {
        setCurrentPage(data.pagination.currentPage);
      }
    } catch (error) {

      setNotification({
        type: 'error',
        message: 'Failed to load subscribers. Please try again.'
      });
      // Reset to empty array on error
      setSubscribers([]);
      setPagination(null);
    } finally {
      setIsLoading(false);
      setIsFetching(false);
    }
  }, []);

  // Handle view subscriber details
  const handleViewSubscriber = (subscriberId: string) => {
    navigate(`/panel/admin/subscriber-management/${subscriberId}`);
  };

  const totalItems = pagination?.totalUsers || 0;
  const totalPages = pagination?.totalPages || 1;

  // Load initial data
  useEffect(() => {
    fetchSubscribers(1, '', 'all', dateFrom, dateTo);
  }, [fetchSubscribers, dateFrom, dateTo]);

  // Handle search and filter changes
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      // Reset to page 1 and clear selections when search/filter changes
      setCurrentPage(1);
      setSelectedSubscribers(new Set());
      fetchSubscribers(1, searchTerm, statusFilter, dateFrom, dateTo);
    }, 300); // Debounce search

    return () => clearTimeout(debounceTimer);
  }, [searchTerm, statusFilter, dateFrom, dateTo, fetchSubscribers]);

  // Handle page changes (not triggered by search/filter)
  useEffect(() => {
    // Only fetch if we have a valid page number and are not in initial loading state
    if (currentPage >= 1) {
      fetchSubscribers(currentPage, searchTerm, statusFilter, dateFrom, dateTo);
    }
  }, [currentPage, searchTerm, statusFilter, dateFrom, dateTo, fetchSubscribers]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!(event.target as Element).closest('.subscriber-management__filter-dropdown')) {
        setShowStatusDropdown(false);
        setShowDateDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Auto-dismiss notifications after 5 seconds
  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => {
        setNotification(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  // Use subscribers directly (API handles pagination)

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handlePrevPage = () => {
    if (pagination?.hasPrev && currentPage > 1) {
      setCurrentPage(Math.max(1, currentPage - 1));
    }
  };

  const handleNextPage = () => {
    if (pagination?.hasNext) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Checkbox handlers
  const handleSelectAll = () => {
    const subscriberCount = subscribers?.length || 0;
    if (selectedSubscribers.size === subscriberCount) {
      // Deselect all
      setSelectedSubscribers(new Set());
    } else {
      // Select all current page subscribers
      setSelectedSubscribers(new Set(subscribers?.map(sub => sub.id) || []));
    }
  };

  const handleSelectSubscriber = (subscriberId: string) => {
    const newSelected = new Set(selectedSubscribers);
    if (newSelected.has(subscriberId)) {
      newSelected.delete(subscriberId);
    } else {
      newSelected.add(subscriberId);
    }
    setSelectedSubscribers(newSelected);
  };

  // Bulk action handlers
  const handleDeleteSubscriptions = async () => {
    if (selectedSubscribers.size === 0) return;

    setIsLoading(true);
    try {
      // Get selected subscribers
      const selectedData = subscribers.filter(sub => selectedSubscribers.has(sub.id));
      const freeUsers = selectedData.filter(sub => sub.status === 'free');
      const paidUsers = selectedData.filter(sub => sub.status !== 'free');

      // Show different messages for free vs paid users
      if (freeUsers.length > 0 && paidUsers.length === 0) {
        setNotification({
          type: 'info',
          message: `Free users (${freeUsers.length}) cannot have subscriptions deleted.`
        });
      } else if (paidUsers.length > 0) {
        let successCount = 0;
        let errorCount = 0;

        // Delete each paid subscription
        for (const user of paidUsers) {
          try {
            if (!user.subscriptionId) {

              errorCount++;
              continue;
            }

            const response = await fetch(`${API_CONFIG.ENDPOINTS.BILLING_SUBSCRIPTION}/${user.subscriptionId}`, {
              method: 'DELETE',
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`,
                'Content-Type': 'application/json'
              }
            });

            if (!response.ok) {
              const errorData = await response.json();

              errorCount++;
            } else {
              successCount++;
            }
          } catch (error) {

            errorCount++;
          }
        }

        // Show appropriate notification based on results
        if (successCount > 0) {
          setNotification({
            type: successCount === paidUsers.length ? 'success' : 'info',
            message: `Successfully deleted ${successCount} subscription${successCount > 1 ? 's' : ''}.${errorCount > 0 ? ` ${errorCount} failed.` : ''}${freeUsers.length > 0 ? ` Free users (${freeUsers.length}) were skipped.` : ''}`
          });

          // Refresh subscriber data after successful deletions
          if (successCount > 0) {
            await fetchSubscribers(currentPage, searchTerm, statusFilter, dateFrom, dateTo);
          }
        } else {
          setNotification({
            type: 'error',
            message: `Failed to delete any subscriptions.${freeUsers.length > 0 ? ` Free users (${freeUsers.length}) were skipped.` : ''}`
          });
        }

        // Clear selections after operation
        setSelectedSubscribers(new Set());
      }
    } catch (error) {

      setNotification({
        type: 'error',
        message: 'Failed to delete subscriptions. Please try again.'
      });
    } finally {
      setIsLoading(false);
      setShowDeleteModal(false);
    }
  };

  // Fetch available plans for the extend modal
  const fetchAvailablePlans = async () => {
    try {
      const response = await fetch(API_CONFIG.ENDPOINTS.BILLING_PLANS, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch plans');
      }

      const data = await response.json();
      setAvailablePlans(data.data || []);
    } catch (error) {

      // Set some default plans if fetch fails
      setAvailablePlans([
        { _id: 'plan_default', name: 'Default Plan', price: 0, currency: 'PHP' }
      ]);
    }
  };

  const handleExtendSubscriptions = async () => {
    if (!extendDays) return;

    const days = parseInt(extendDays);
    if (isNaN(days) || days <= 0 || days > 365) {
      setNotification({
        type: 'error',
        message: 'Please enter a valid number of days (1-365).'
      });
      return;
    }

    setIsLoading(true);
    try {
      // Build filter object based on current filters
      const filter: any = {};

      if (statusFilter !== 'all') {
        filter.status = statusFilter;
      }

      if (searchTerm.trim()) {
        filter.search = searchTerm.trim();
      }

      // Call bulk add-days API
      const response = await fetch(API_CONFIG.ENDPOINTS.USERS_ADMIN_BULK_ADD_DAYS, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          days: days,
          planIdForFree: extendPlanId || undefined, // Will be set in modal
          filter: filter,
          reason: `Bulk extension via subscriber management (${statusFilter} status${searchTerm ? ', search: ' + searchTerm : ''})`
        })
      });

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.message || 'Failed to add days to subscriptions');
      }

      // Show success message with details
      const { totalUsersFound, totalAffected, totalErrors } = result.data;
      let message = `Successfully added ${days} days to ${totalAffected} out of ${totalUsersFound} users.`;

      if (totalErrors > 0) {
        message += ` ${totalErrors} operations failed.`;
      }

      if (totalAffected === 0) {
        setNotification({
          type: 'info',
          message: `No users matched the current filters or no valid operations could be performed.`
        });
      } else {
        setNotification({
          type: 'success',
          message: message
        });

        // Refresh the subscriber list
        fetchSubscribers(currentPage, searchTerm, statusFilter, dateFrom, dateTo);
      }

      // Clear form after successful operation
      setExtendDays('');
      setExtendPlanId('');

    } catch (error) {

      setNotification({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to add days to subscriptions. Please try again.'
      });
    } finally {
      setIsLoading(false);
      setShowExtendModal(false);
    }
  };


  const handleExportSubscribers = () => {
    if (selectedSubscribers.size === 0) return;

    try {
      const selectedData = subscribers.filter(sub => selectedSubscribers.has(sub.id));

      // Create CSV content
      const headers = ['ID', 'User ID', 'Name', 'Email', 'Plan', 'Status', 'Start Date', 'End Date', 'Has Subscription', 'Subscription ID'];
      const csvContent = [
        headers.join(','),
        ...selectedData.map(sub => [
          sub.id,
          sub.userId,
          `"${sub.name}"`,
          sub.email,
          sub.plan,
          sub.status,
          sub.startDate || '',
          sub.endDate || '',
          sub.hasSubscription.toString(),
          sub.subscriptionId || ''
        ].join(','))
      ].join('\n');

      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `subscribers_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setNotification({
        type: 'success',
        message: `Successfully exported ${selectedSubscribers.size} subscriber${selectedSubscribers.size > 1 ? 's' : ''} to CSV.`
      });

      // Clear selections after successful operation
      setSelectedSubscribers(new Set());
    } catch (error) {
      setNotification({
        type: 'error',
        message: 'Failed to export subscribers. Please try again.'
      });
    }
  };
  return (
    <div className="subscriber-management">
      <main className="subscriber-management__main">
        <div className="subscriber-management__container">
          {/* Header Section */}
          <div className="subscriber-management__header">
            <div className="subscriber-management__header-content">
              <div className="subscriber-management__title-section">
                <h1 className="subscriber-management__title">Subscriber Management</h1>
                <p className="subscriber-management__subtitle">Manage, filter, and take action on your subscribers.</p>
              </div>
            </div>
          </div>

          {/* Filters Section */}
          <div className="subscriber-management__filters">
            <div className="subscriber-management__search-container">
              <div className="subscriber-management__search-input-wrapper">
                <span className="material-symbols-outlined subscriber-management__search-input-wrapper__search-icon">search</span>
                <input
                  type="text"
                  className="subscriber-management__search-input-wrapper__search-input"
                  placeholder="Search by name, email, or ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                {searchTerm && (
                  <button
                    type="button"
                    className="subscriber-management__search-clear"
                    onClick={() => setSearchTerm('')}
                    aria-label="Clear search"
                  >
                    <span className="material-symbols-outlined">close</span>
                  </button>
                )}
              </div>
            </div>
            <div className="subscriber-management__filter-dropdown">
              <button
                className={`subscriber-management__filter-btn ${dateFrom || dateTo ? 'subscriber-management__filter-btn--active' : ''}`}
                onClick={() => setShowDateDropdown(!showDateDropdown)}
              >
                <div className="subscriber-management__filter-content">
                  <span className="material-symbols-outlined">calendar_month</span>
                  <p>
                    {dateFrom || dateTo
                      ? `Date Subscribed: ${dateFrom ? formatDate(dateFrom) : '...'} - ${dateTo ? formatDate(dateTo) : '...'}`
                      : 'Date Subscribed'
                    }
                  </p>
                </div>
                <span className="material-symbols-outlined subscriber-management__arrow">expand_more</span>
              </button>
              {showDateDropdown && (
                <div className="subscriber-management__dropdown-menu">
                  <div className="subscriber-management__date-filter-content">
                    <div className="subscriber-management__date-input-group">
                      <label className="subscriber-management__date-label">From Date</label>
                      <input
                        type="date"
                        className="subscriber-management__date-input"
                        value={dateFrom}
                        onChange={(e) => setDateFrom(e.target.value)}
                        max={dateTo || undefined}
                      />
                    </div>
                    <div className="subscriber-management__date-input-group">
                      <label className="subscriber-management__date-label">To Date</label>
                      <input
                        type="date"
                        className="subscriber-management__date-input"
                        value={dateTo}
                        onChange={(e) => setDateTo(e.target.value)}
                        min={dateFrom || undefined}
                      />
                    </div>
                    <div className="subscriber-management__date-actions">
                      <button
                        className="subscriber-management__date-clear-btn"
                        onClick={() => {
                          setDateFrom('');
                          setDateTo('');
                          setShowDateDropdown(false);
                        }}
                        disabled={!dateFrom && !dateTo}
                      >
                        Clear
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="subscriber-management__filter-dropdown">
              <button
                className={`subscriber-management__filter-btn ${statusFilter !== 'all' ? 'subscriber-management__filter-btn--active' : ''}`}
                onClick={() => setShowStatusDropdown(!showStatusDropdown)}
              >
                <div className="subscriber-management__filter-content">
                  <span className="material-symbols-outlined">verified</span>
                  <p>Status: {statusFilter === 'all' ? 'All' : statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1)}</p>
                </div>
                <span className="material-symbols-outlined subscriber-management__arrow">expand_more</span>
              </button>
              {showStatusDropdown && (
                <div className="subscriber-management__dropdown-menu">
                  <button
                    className="subscriber-management__dropdown-item"
                    onClick={() => { setStatusFilter('all'); setShowStatusDropdown(false); }}
                  >
                    All
                  </button>
                  <button
                    className="subscriber-management__dropdown-item"
                    onClick={() => { setStatusFilter('active'); setShowStatusDropdown(false); }}
                  >
                    Active
                  </button>
                  <button
                    className="subscriber-management__dropdown-item"
                    onClick={() => { setStatusFilter('expired'); setShowStatusDropdown(false); }}
                  >
                    Expired
                  </button>
                  <button
                    className="subscriber-management__dropdown-item"
                    onClick={() => { setStatusFilter('cancelled'); setShowStatusDropdown(false); }}
                  >
                    Cancelled
                  </button>
                  <button
                    className="subscriber-management__dropdown-item"
                    onClick={() => { setStatusFilter('free'); setShowStatusDropdown(false); }}
                  >
                    Free
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Table Section */}
          <div className="subscriber-management__table-container">
            {/* Table Header */}
            <div className="subscriber-management__table-header">
                  <p className="subscriber-management__table-info">Showing {Math.max(1, ((currentPage - 1) * 5) + 1)}-{Math.max(1, ((currentPage - 1) * 5) + (subscribers?.length || 0))} of {totalItems} subscribers</p>
              <div className="subscriber-management__table-actions">
                <button
                  className={`subscriber-management__action-btn ${selectedSubscribers.size === 0 ? 'subscriber-management__action-btn--disabled' : ''}`}
                  disabled={selectedSubscribers.size === 0}
                  onClick={() => setShowDeleteModal(true)}
                  title="Delete selected subscriptions"
                >
                  <span className="material-symbols-outlined">delete</span>
                  <span className="sr-only">Delete</span>
                </button>
                <button
                  className="subscriber-management__action-btn"
                  onClick={() => {
                    fetchAvailablePlans();
                    setShowExtendModal(true);
                  }}
                  title="Add days to all filtered subscriptions"
                >
                  <span className="material-symbols-outlined">schedule</span>
                  <span className="sr-only">Extend Days</span>
                </button>
                <button
                  className={`subscriber-management__action-btn ${selectedSubscribers.size === 0 ? 'subscriber-management__action-btn--disabled' : ''}`}
                  disabled={selectedSubscribers.size === 0}
                  onClick={handleExportSubscribers}
                  title="Export selected subscribers"
                >
                  <span className="material-symbols-outlined">download</span>
                  <span className="sr-only">Export</span>
                </button>
              </div>
            </div>

            {/* Table */}
            <div className="subscriber-management__table-wrapper">
              {isFetching && (
                <div className="subscriber-management__loading-overlay">
                  <LoadingSpinner />
                  <span>Loading subscribers...</span>
                </div>
              )}
              <table className="subscriber-management__table">
                <thead className="subscriber-management__table-head">
                  <tr>
                    <th className="subscriber-management__table-th subscriber-management__table-th--checkbox" scope="col">
                      <div className="subscriber-management__checkbox-wrapper">
                        <input
                          className="subscriber-management__checkbox"
                          id="checkbox-all"
                          type="checkbox"
                          checked={(subscribers?.length || 0) > 0 && selectedSubscribers.size === (subscribers?.length || 0)}
                          onChange={handleSelectAll}
                          disabled={isFetching}
                        />
                        <label className="sr-only" htmlFor="checkbox-all">Select all subscribers</label>
                      </div>
                    </th>
                    <th className="subscriber-management__table-th" scope="col">User</th>
                    <th className="subscriber-management__table-th" scope="col">Plan</th>
                    <th className="subscriber-management__table-th" scope="col">Start Date</th>
                    <th className="subscriber-management__table-th" scope="col">End Date</th>
                    <th className="subscriber-management__table-th" scope="col">Status</th>
                    <th className="subscriber-management__table-th" scope="col">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    // Loading skeleton rows
                    Array.from({ length: 5 }, (_, index) => (
                      <tr key={`loading-${index}`} className="subscriber-management__table-row">
                        <td className="subscriber-management__table-td subscriber-management__table-td--checkbox">
                          <div className="subscriber-management__skeleton"></div>
                        </td>
                        <td className="subscriber-management__table-td">
                          <div className="subscriber-management__user-info">
                            <div className="subscriber-management__skeleton subscriber-management__skeleton--name"></div>
                            <div className="subscriber-management__skeleton subscriber-management__skeleton--email"></div>
                          </div>
                        </td>
                        <td className="subscriber-management__table-td">
                          <div className="subscriber-management__skeleton subscriber-management__skeleton--small"></div>
                        </td>
                        <td className="subscriber-management__table-td">
                          <div className="subscriber-management__skeleton subscriber-management__skeleton--small"></div>
                        </td>
                        <td className="subscriber-management__table-td">
                          <div className="subscriber-management__skeleton subscriber-management__skeleton--small"></div>
                        </td>
                        <td className="subscriber-management__table-td">
                          <div className="subscriber-management__skeleton subscriber-management__skeleton--small"></div>
                        </td>
                        <td className="subscriber-management__table-td">
                          <div className="subscriber-management__skeleton subscriber-management__skeleton--small"></div>
                        </td>
                      </tr>
                    ))
                  ) : (subscribers?.length || 0) === 0 ? (
                    <tr className="subscriber-management__table-row">
                      <td colSpan={7} className="subscriber-management__table-td subscriber-management__table-td--empty">
                        No subscribers found
                      </td>
                    </tr>
                  ) : (
                    (subscribers || []).map((subscriber) => (
                    <tr key={subscriber.id} className="subscriber-management__table-row">
                      <td className="subscriber-management__table-td subscriber-management__table-td--checkbox">
                        <div className="subscriber-management__checkbox-wrapper">
                          <input
                            className="subscriber-management__checkbox"
                            id={`checkbox-${subscriber.id}`}
                            type="checkbox"
                            checked={selectedSubscribers.has(subscriber.id)}
                            onChange={() => handleSelectSubscriber(subscriber.id)}
                          />
                          <label className="sr-only" htmlFor={`checkbox-${subscriber.id}`}>Select {subscriber.name}</label>
                        </div>
                      </td>
                      <th className="subscriber-management__table-td subscriber-management__table-td--user" scope="row">
                        <div className="subscriber-management__user-info">
                          <div className="subscriber-management__user-name">{subscriber.name}</div>
                          <div className="subscriber-management__user-email">{subscriber.email}</div>
                        </div>
                      </th>
                      <td className="subscriber-management__table-td">{subscriber.plan}</td>
                      <td className="subscriber-management__table-td">{subscriber.startDate ? formatDate(subscriber.startDate) : '\u00A0'}</td>
                      <td className="subscriber-management__table-td">{subscriber.endDate ? formatDate(subscriber.endDate) : '\u00A0'}</td>
                      <td className="subscriber-management__table-td">
                        <div className="subscriber-management__status-wrapper">
                          <span className={`subscriber-management__status-badge subscriber-management__status-badge--${subscriber.status}`}>
                            <span className="subscriber-management__status-dot"></span>
                            {subscriber.status.charAt(0).toUpperCase() + subscriber.status.slice(1)}
                          </span>
                        </div>
                      </td>
                      <td className="subscriber-management__table-td">
                        <button
                          className="subscriber-management__action-link"
                          type="button"
                          onClick={() => handleViewSubscriber(subscriber.id)}
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <nav aria-label="Table navigation" className="subscriber-management__pagination">
              <span className="subscriber-management__pagination-info">
                Showing <span className="subscriber-management__pagination-highlight">{Math.max(1, ((currentPage - 1) * 5) + 1)}-{Math.max(1, ((currentPage - 1) * 5) + (subscribers?.length || 0))}</span> of <span className="subscriber-management__pagination-highlight">{totalItems}</span>
              </span>
              <ul className="subscriber-management__pagination-list">
                <li>
                  <button
                    className="subscriber-management__pagination-link subscriber-management__pagination-link--previous"
                    onClick={handlePrevPage}
                    disabled={!pagination?.hasPrev}
                  >
                    Previous
                  </button>
                </li>
                {Array.from({ length: totalPages }, (_, index) => index + 1).map((page) => (
                  <li key={page}>
                    <button
                      className={`subscriber-management__pagination-link ${
                        page === currentPage ? 'subscriber-management__pagination-link--active' : ''
                      }`}
                      onClick={() => handlePageChange(page)}
                      aria-current={page === currentPage ? 'page' : undefined}
                    >
                      {page}
                    </button>
                  </li>
                ))}
                <li>
                  <button
                    className="subscriber-management__pagination-link"
                    onClick={handleNextPage}
                    disabled={!pagination?.hasNext}
                  >
                    Next
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      </main>

      {/* Cancel Confirmation Modal */}
      {showDeleteModal && (
        <div className="subscriber-management__modal-overlay">
          <div className="subscriber-management__modal">
            <div className="subscriber-management__modal-header">
              <h3 className="subscriber-management__modal-title">Delete Subscriptions</h3>
              <button
                className="subscriber-management__modal-close"
                onClick={() => setShowDeleteModal(false)}
              >
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>
            <div className="subscriber-management__modal-body">
              <p>Are you sure you want to permanently delete {selectedSubscribers.size} subscription{selectedSubscribers.size > 1 ? 's' : ''}?</p>
              <p className="subscriber-management__modal-warning">
                ⚠️ This action cannot be undone. All subscription data will be permanently removed.
              </p>
              <p className="subscriber-management__modal-note">
                Note: Free users will be skipped automatically.
              </p>
            </div>
            <div className="subscriber-management__modal-footer">
              <button
                className="subscriber-management__modal-btn subscriber-management__modal-btn--cancel"
                onClick={() => setShowDeleteModal(false)}
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                className="subscriber-management__modal-btn subscriber-management__modal-btn--danger"
                onClick={handleDeleteSubscriptions}
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <LoadingSpinner />
                    Deleting...
                  </>
                ) : (
                  'Confirm Delete'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Days Modal */}
      {showExtendModal && (
        <div className="subscriber-management__modal-overlay">
          <div className="subscriber-management__modal">
            <div className="subscriber-management__modal-header">
              <h3 className="subscriber-management__modal-title">Add Days to Subscriptions</h3>
              <button
                className="subscriber-management__modal-close"
                onClick={() => setShowExtendModal(false)}
              >
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>
            <div className="subscriber-management__modal-body">
              <p className="bulk-add-days-modal__description">Add days to <strong>all users matching current filters</strong>:</p>

              <div className="bulk-add-days-modal__form-group">
                <label className="bulk-add-days-modal__label">
                  Days to Add *
                </label>
                <div className="bulk-add-days-modal__extend-input">
                  <input
                    type="number"
                    min="1"
                    max="365"
                    value={extendDays}
                    onChange={(e) => setExtendDays(e.target.value)}
                    placeholder="Enter days"
                    className="subscriber-management__input"
                  />
                  <span>days</span>
                </div>
              </div>

              <div className="bulk-add-days-modal__form-group">
                <label className="bulk-add-days-modal__label">
                  Plan for Free Users *
                </label>
                <select
                  value={extendPlanId}
                  onChange={(e) => setExtendPlanId(e.target.value)}
                  className="subscriber-management__input"
                >
                  <option value="">Select a plan...</option>
                  {availablePlans.map((plan) => (
                    <option key={plan._id} value={plan._id}>
                      {plan.name} - {plan.currency} {plan.price?.toFixed(2) || 'Contact us'}
                    </option>
                  ))}
                </select>
              </div>

              <div className="bulk-add-days-modal__current-filters">
                <p>Current Filters Applied:</p>
                <p>Status: {statusFilter === 'all' ? 'All Users' : statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1)}
                  {searchTerm && ` | Search: "${searchTerm}"`}</p>
              </div>
            </div>
            <div className="subscriber-management__modal-footer">
              <button
                className="subscriber-management__modal-btn subscriber-management__modal-btn--cancel"
                onClick={() => setShowExtendModal(false)}
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                className="subscriber-management__modal-btn subscriber-management__modal-btn--confirm"
                onClick={handleExtendSubscriptions}
                disabled={isLoading || !extendDays || !extendPlanId}
              >
                {isLoading ? (
                  <>
                    <LoadingSpinner />
                    Adding Days...
                  </>
                ) : (
                  'Add Days'
                )}
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Notification Toast */}
      {notification && (
        <div className={`subscriber-management__notification subscriber-management__notification--${notification.type}`}>
          <span className="material-symbols-outlined">
            {notification.type === 'success' ? 'check_circle' :
             notification.type === 'error' ? 'error' : 'info'}
          </span>
          <span>{notification.message}</span>
          <button
            className="subscriber-management__notification-close"
            onClick={() => setNotification(null)}
          >
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default SubscriberManagementPage;

